/** @file Draggable points of the selection: socket a/b (parent-bone space) and item handle a/b/c (image px). */
import type { Handle, Id } from '@pose2d/format/types';
import { slotWorld } from '@pose2d/geometry/slotWorld';
import { affine } from '@pose2d/math/affine';
import type { Affine, Vec2 } from '@pose2d/math/types';
import type { ToolContext } from '../../panels/viewport/viewportTypes';
import { parentWorldOf } from '../transform/parentWorldOf';
import { handleFrame } from './handleFrame';

/** `toLocal` maps world back into the space the command takes. */
export type PointTarget =
  | { kind: 'socket'; socket: Id; bone: Id; which: 'a' | 'b'; world: Vec2; toLocal: Affine; a: Vec2; b: Vec2 }
  | { kind: 'handle'; attachment: Id; which: 'a' | 'b' | 'c'; world: Vec2; toLocal: Affine; handle: Handle };

/** a and b of every socket whose socket bone is selected, in the socket bone's parent (the socket's parentBone). */
function socketTargets(ctx: ToolContext): PointTarget[] {
  return Object.entries(ctx.doc.sockets).flatMap(([socket, s]): PointTarget[] => {
    const i = ctx.maps.boneIndex.get(s.bone);
    const parent = i === undefined || !ctx.selection.bones.includes(s.bone) ? null : parentWorldOf(ctx.pose, ctx.data, i);
    if (parent === null) return [];
    const toLocal = affine.invert(new Float64Array(6), parent);
    return (['a', 'b'] as const).map((which) => ({ kind: 'socket', socket, bone: s.bone, which, world: affine.apply(parent, s[which][0], s[which][1], [0, 0]), toLocal, a: s.a, b: s.b }));
  });
}

/** The document attachment a slot shows now, with its id; null when the slot shows nothing. */
function shownAttachment(ctx: ToolContext, slot: number): { id: Id; handle: Handle | undefined; index: number } | null {
  const index = ctx.pose.slotAttachment[slot] ?? -1;
  const name = ctx.data.attachments[index]?.name;
  const found = name === undefined ? undefined : Object.entries(ctx.doc.attachments).find(([, a]) => a.name === name);
  return found === undefined ? null : { id: found[0], handle: found[1].handle, index };
}

/** a, b (and c) of the handle of every selected slot's current attachment, through the compiled attachment frame. */
function handleTargets(ctx: ToolContext): PointTarget[] {
  return ctx.selection.slots.flatMap((slotId): PointTarget[] => {
    const slot = ctx.maps.slotIndex.get(slotId);
    const shown = slot === undefined ? null : shownAttachment(ctx, slot);
    const handle = shown?.handle;
    const frame = shown === null ? null : handleFrame(ctx.json, ctx.atlas, shown.index);
    if (slot === undefined || shown === null || handle === undefined || frame === null) return [];
    const full = affine.mul(new Float64Array(6), slotWorld(ctx.pose, ctx.data, slot, new Float64Array(6)), frame);
    const toLocal = affine.invert(new Float64Array(6), full);
    return (['a', 'b', 'c'] as const).flatMap((which): PointTarget[] => {
      const px = handle[which];
      return px === undefined ? [] : [{ kind: 'handle', attachment: shown.id, which, world: affine.apply(full, px[0], px[1], [0, 0]), toLocal, handle }];
    });
  });
}

/** Socket points first, then handle points. */
export function pointTargets(ctx: ToolContext): PointTarget[] {
  return [...socketTargets(ctx), ...handleTargets(ctx)];
}
